import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import type {
  AddressData,
  PropertyFormData,
  PropertyRealStateDetailInfo,
} from "../model/propertiesTypes";
import { submitPropertyProtestApi } from "../model/propertiesApi";

/**
 * Properties ViewModel
 * Manages properties stepper state and business logic
 */
type PropertiesLocationState = {
  addressData?: AddressData;
  propertyDetails?: PropertyRealStateDetailInfo;
  initialStep?: unknown;
  postAuthShowLanding?: boolean;
};

const totalSteps = 4;

function initialStepFromLocationState(s: PropertiesLocationState | null | undefined): number {
  if (!s) return 1;
  if (s.postAuthShowLanding) return 1;
  const raw = s.initialStep;
  if (typeof raw === "number" && raw >= 1 && raw <= totalSteps) {
    return raw;
  }
  return 1;
}

export const usePropertiesViewModel = () => {
  const location = useLocation();
  const state = location.state as PropertiesLocationState | null | undefined;

  const propertyDetails = state?.propertyDetails;
  const addressData = state?.addressData;

  const [currentStep, setCurrentStep] = useState(() => initialStepFromLocationState(state));
  const [formData, setFormData] = useState<PropertyFormData>({});

  /** New history entry (e.g. address search or post-login restore only): align step from route state. Intentionally keyed by `location.key` only so in-flow next/back does not reset the step. */
  useEffect(() => {
    setCurrentStep(
      initialStepFromLocationState(location.state as PropertiesLocationState | null | undefined)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- use `location.key` as the only sync trigger; `location.state` must not re-run this when the user advances steps on the same entry
  }, [location.key]);

  useEffect(() => {
    setFormData({});
  }, [location.key]);

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStep2Next = (data: { firstName?: string; lastName?: string }) => {
    setFormData((prev) => ({ ...prev, ...data }));
    handleNext();
  };

  const handleStep3Next = (data: { userType: "owner" | "tenant" }) => {
    setFormData((prev) => ({ ...prev, ...data }));
    handleNext();
  };

  const handleSubmit = async () => {
    try {
      await submitPropertyProtestApi({ addressData, formData });
      console.log("Property protest submitted successfully");
    } catch (error) {
      console.error("Error submitting property protest:", error);
      throw error;
    }
  };

  return {
    addressData,
    currentStep,
    totalSteps,
    formData,
    propertyDetails,
    handleNext,
    handleBack,
    handleStep2Next,
    handleStep3Next,
    handleSubmit,
  };
};
